import { ReactElement, ReactNode } from 'react';
import { TabsProps } from 'antd';
import { useTranslation } from 'react-i18next';
import { Outlet, matchPath, useLocation, useNavigate, useParams } from 'react-router-dom';
import { Button } from 'common/components/antd/Button/Button';
import { Tabs } from 'common/components/antd/Tabs/Tabs';
import { IS_ADMIN_FE, IS_BANK_FE } from 'common/constants/general-access-role.constants';
import { ROUTES_MAPPING } from 'navigation/constants/route.constants';
import { ERoutePathParams } from 'navigation/enums/rout-path-params.enum';
import {
  STitle,
  SHeader,
  SContent,
  SContainer,
  STabs,
} from './account-management.layout.style';
import { HeaderComponent } from './components/Header/Header.component';
import {
  EAccountManagementCounterpartyEmissionPageGetParams,
  EAccountManagementCounterpartyEmissionPageGetParamsActionTypes,
} from '../counterparty-emission/enums/counerparty-emisson.enums';
import {
  EAccountManagementCounterpartyProcessingPageGetParams,
  EAccountManagementCounterpartyProcessingPageGetParamsActionTypes,
} from '../counterparty-processing/enums/counerparty-processing.enums';
import {
  EAccountManagementCustomersPageGetParams,
  EAccountManagementCustomersPageGetParamsActionTypes,
} from '../customers/enums/customers.enum';

type Tab = {
  path: string;
  key: string;
  label: ReactNode;
  extraContent: ReactNode;
};

const ESectionKey = {
  CounterpartyEmission: 'counterparty-emission',
  CounterpartyProcessing: 'counterparty-processing',
  Customers: 'customers',
};

const AccountManagementLayout = (): ReactElement => {
  const navigate = useNavigate();

  const location = useLocation();
  const { t } = useTranslation();
  const { counterparty } = useParams();

  const tabs: Tab[] = [
    {
      key: ESectionKey.CounterpartyEmission,
      label: t('accountManagement.tabs.counterpartyEmission.title'),
      path: ROUTES_MAPPING.PROTECTED.ACCOUNT_MANAGEMENT_COUNTERPARTY_EMISSION.replace(
        ERoutePathParams.Counterparty,
        counterparty || '',
      ),
      extraContent: IS_ADMIN_FE && (
        <div
          style={{
            marginRight: 16,
          }}
        >
          <Button
            onClick={(): void => {
              const url = ROUTES_MAPPING.PROTECTED.ACCOUNT_MANAGEMENT_COUNTERPARTY_EMISSION;
              const actionGetParam = EAccountManagementCounterpartyEmissionPageGetParams.Action;
              const actionGetParamValue = EAccountManagementCounterpartyEmissionPageGetParamsActionTypes.Create;
              navigate(`${url}?${actionGetParam}=${actionGetParamValue}`);
            }}
            type="primary"
          >
            {t('accountManagement.tabs.counterpartyEmission.secondaryAction')}
          </Button>
        </div>
      ),
    },
    {
      key: ESectionKey.CounterpartyProcessing,
      label: t('accountManagement.tabs.counterpartyProcessing.title'),
      path: ROUTES_MAPPING.PROTECTED.ACCOUNT_MANAGEMENT_COUNTERPARTY_PROCESSING.replace(
        ERoutePathParams.Counterparty,
        counterparty || '',
      ),
      extraContent: (
        <div
          style={{
            marginRight: 16,
          }}
        >
          <Button
            onClick={(): void => {
              const url = ROUTES_MAPPING.PROTECTED.ACCOUNT_MANAGEMENT_COUNTERPARTY_PROCESSING;
              const actionGetParam = EAccountManagementCounterpartyProcessingPageGetParams.Action;
              const actionGetParamValue = EAccountManagementCounterpartyProcessingPageGetParamsActionTypes.Create;
              navigate(`${url}?${actionGetParam}=${actionGetParamValue}`);
            }}
            type="primary"
          >
            {t('accountManagement.tabs.counterpartyProcessing.secondaryAction')}
          </Button>
        </div>
      ),
    },
    {
      key: ESectionKey.Customers,
      label: t('accountManagement.tabs.customers.title'),
      path: ROUTES_MAPPING.PROTECTED.ACCOUNT_MANAGEMENT_CUSTOMERS,
      extraContent: IS_BANK_FE && (
        <div
          style={{
            marginRight: 16,
          }}
        >
          <Button
            onClick={(): void => {
              const url = ROUTES_MAPPING.PROTECTED.ACCOUNT_MANAGEMENT_CUSTOMERS;
              const actionGetParam = EAccountManagementCustomersPageGetParams.Action;
              const actionGetParamValue = EAccountManagementCustomersPageGetParamsActionTypes.Create;

              navigate(`${url}?${actionGetParam}=${actionGetParamValue}`);
            }}
            type="primary"
          >
            {t('accountManagement.customers.button.create')}
          </Button>
        </div>
      ),
    },
  ];

  const activeTab = tabs.find((tab) => {
    const match = matchPath(
      {
        path: tab.path,
        end: false,
        caseSensitive: false,
      },
      location.pathname,
    );

    return !!match;
  });

  const extraContent = {
    right: activeTab?.extraContent,
    left: <div style={{ width: 24 }} />,
  };

  const onChangeTab: TabsProps['onChange'] = (key) => {
    const tabChangeTo = tabs.find((tab) => tab.key === key);
    if (tabChangeTo) {
      navigate(tabChangeTo.path.replace(ERoutePathParams.Counterparty, counterparty || ''));
    }
  };

  return (
    <SContainer>
      <STitle>{t('accountManagement.title')}</STitle>
      <SHeader>
        <HeaderComponent />
      </SHeader>
      <SContent>
        <STabs>
          <Tabs
            onChange={onChangeTab}
            activeKey={activeTab?.key}
            tabBarExtraContent={extraContent}
            defaultActiveKey="1"
            items={tabs}
          />
          <Outlet />
        </STabs>
      </SContent>
    </SContainer>
  );
};

export default AccountManagementLayout;
